// Edit Profile page - Mobile parity version
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPatch, apiUpload, apiDelete, API_BASE_URL } from "../lib/api";
import { useAuth } from "../context/AuthProvider";
import ImageCropper from "../components/ImageCropper";

interface User {
  id: string;
  username: string;
  email: string;
  bio?: string | null;
  favoriteQuote?: string | null;
  website?: string | null;
  avatarUrl?: string | null;
  avatarUrlSmall?: string | null;
}

function resolveImg(url?: string | null): string | undefined {
  if (!url) return undefined;
  return url.startsWith("/") ? `${API_BASE_URL}${url}` : url;
}

export default function EditProfile() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  
  const [bio, setBio] = useState("");
  const [favoriteQuote, setFavoriteQuote] = useState("");
  const [website, setWebsite] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarUrlSmall, setAvatarUrlSmall] = useState<string | null>(null);
  
  // Crop state
  const [showCropper, setShowCropper] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);
  
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await apiGet<{ ok: true; user: User }>("/api/profile/me");
      const profile = response.user;
      
      setBio(profile.bio || "");
      setFavoriteQuote(profile.favoriteQuote || "");
      setWebsite(profile.website || "");
      setAvatarUrl(profile.avatarUrl || null);
      setAvatarUrlSmall(profile.avatarUrlSmall || null);
    } catch (error) {
      console.error("Profil yüklenemedi:", error);
      alert("Profil bilgileri yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      // Profil bilgilerini güncelle
      await apiPatch("/api/profile/me", {
        bio: bio.trim() || null,
        favoriteQuote: favoriteQuote.trim() || null,
        website: website.trim() || null,
      });
      
      alert("Profil güncellendi!");
      await refreshMe();
      navigate(-1);
    } catch (error: any) {
      console.error("Profil güncellenemedi:", error);
      alert(error?.message || "Profil güncellenemedi.");
    } finally {
      setSaving(false);
    }
  };

  const pickImage = () => {
    fileRef.current?.click();
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const okTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!okTypes.includes(file.type)) {
      alert("Sadece JPEG/PNG/WebP dosyası yükleyebilirsin.");
      e.target.value = "";
      return;
    }
    const max = 5 * 1024 * 1024;
    if (file.size > max) {
      alert("Dosya boyutu 5 MB'ı aşamaz.");
      e.target.value = "";
      return;
    }

    // Show cropper instead of direct upload
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImageSrc(reader.result as string);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    
    // Reset file input
    e.target.value = "";
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    setShowCropper(false);
    setSelectedImageSrc(null);
    setUploadingAvatar(true);

    try {
      const fd = new FormData();
      fd.append("avatar", croppedBlob, "avatar.jpg");
      const res = await apiUpload<{ ok: true; user: User }>("/api/profile/avatar", fd);

      setAvatarUrl(res.user.avatarUrl || null);
      setAvatarUrlSmall(res.user.avatarUrlSmall || null);
      alert("Profil fotoğrafı güncellendi!");
      await refreshMe();
    } catch (error: any) {
      alert(error?.message || "Yükleme başarısız oldu.");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setSelectedImageSrc(null);
  };

  const removeAvatar = async () => {
    if (!avatarUrl && !avatarUrlSmall) return;
    if (!confirm("Profil fotoğrafını kaldırmak istiyor musun?")) return;
    
    setUploadingAvatar(true);
    try {
      const res = await apiDelete<{ ok: true; user: User }>("/api/profile/avatar");
      setAvatarUrl(res.user.avatarUrl || null);
      setAvatarUrlSmall(res.user.avatarUrlSmall || null);
      alert("Profil fotoğrafı kaldırıldı.");
      await refreshMe();
    } catch (error: any) {
      alert(error?.message || "Silme işlemi başarısız oldu.");
    } finally {
      setUploadingAvatar(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh",
        backgroundColor: "#000",
        color: "#fff",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        gap: 12,
      }}>
        <div style={{ fontSize: 16, color: "#ccc" }}>Yükleniyor...</div>
      </div>
    );
  }

  const hasAvatar = Boolean(avatarUrl || avatarUrlSmall);

  return (
    <div style={{
      minHeight: "100vh",
      backgroundColor: "#000",
      color: "#fff",
    }}>
      {/* Image Cropper Modal */}
      {showCropper && selectedImageSrc && (
        <ImageCropper
          imageSrc={selectedImageSrc}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}

      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 16px",
        borderBottom: "2px solid #114232",
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            minWidth: 80,
            padding: "8px 0",
            backgroundColor: "transparent",
            border: "none",
            color: "#999",
            fontSize: 16,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          ← İptal
        </button>
        
        <h1 style={{
          color: "#114232",
          fontSize: 20,
          fontWeight: 800,
          margin: 0,
        }}>
          Profili Düzenle
        </h1>
        
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            minWidth: 80,
            padding: "8px 12px",
            backgroundColor: "#114232",
            border: "none",
            borderRadius: 8,
            color: "#fff",
            fontSize: 16,
            fontWeight: 700,
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.5 : 1,
          }}
        >
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: 800,
        margin: "0 auto",
        padding: 20,
        paddingBottom: 40,
      }}>
        {/* Avatar Section */}
        <div style={{
          marginBottom: 32,
          textAlign: "center",
        }}>
          <label style={{
            display: "block",
            color: "#114232",
            fontSize: 16,
            fontWeight: 700,
            marginBottom: 16,
          }}>
            Profil Fotoğrafı
          </label>
          
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 16,
          }}>
            <div style={{ position: "relative" }}>
              {avatarUrlSmall || avatarUrl ? (
                <img
                  src={resolveImg(avatarUrlSmall || avatarUrl)}
                  alt={user?.username}
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: 60,
                    border: "3px solid #114232",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div style={{
                  width: 120,
                  height: 120,
                  borderRadius: 60,
                  backgroundColor: "#114232",
                  border: "3px solid #114232",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                  <span style={{
                    color: "#fff",
                    fontSize: 48,
                    fontWeight: 800,
                  }}>
                    {user?.username?.[0]?.toUpperCase() || "?"}
                  </span>
                </div>
              )}
              
              {uploadingAvatar && (
                <div style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  borderRadius: 60,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                  <div style={{ color: "#fff", fontSize: 14 }}>Yükleniyor...</div>
                </div>
              )}
            </div>
            
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={pickImage}
                disabled={uploadingAvatar}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#114232",
                  border: "none",
                  borderRadius: 8,
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: uploadingAvatar ? "not-allowed" : "pointer",
                  opacity: uploadingAvatar ? 0.5 : 1,
                }}
              >
                {hasAvatar ? "Değiştir" : "Fotoğraf Seç"}
              </button>
              
              {hasAvatar && (
                <button
                  onClick={removeAvatar}
                  disabled={uploadingAvatar}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#2c1a1a",
                    border: "1px solid #ff6b6b",
                    borderRadius: 8,
                    color: "#ff6b6b",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: uploadingAvatar ? "not-allowed" : "pointer",
                    opacity: uploadingAvatar ? 0.5 : 1,
                  }}
                >
                  Kaldır
                </button>
              )}
            </div>
          </div>
          
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: "none" }}
            onChange={onFileChange}
          />
        </div>

        {/* Bio */}
        <div style={{ marginBottom: 24 }}>
          <label style={{
            display: "block",
            color: "#114232",
            fontSize: 16,
            fontWeight: 700,
            marginBottom: 8,
          }}>
            Hakkında
          </label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, 500))}
            placeholder="Kendin hakkında birkaç cümle yaz..."
            maxLength={500}
            rows={5}
            style={{
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              backgroundColor: "#1a1a1a",
              border: "2px solid #114232",
              borderRadius: 12,
              padding: 16,
              color: "#fff",
              fontSize: 16,
              resize: "vertical",
              fontFamily: "inherit",
            }}
          />
          <div style={{
            textAlign: "right",
            color: "#666",
            fontSize: 12,
            marginTop: 4,
          }}>
            {bio.length}/500
          </div>
        </div>

        {/* Favorite Quote */}
        <div style={{ marginBottom: 24 }}>
          <label style={{
            display: "block",
            color: "#114232",
            fontSize: 16,
            fontWeight: 700,
            marginBottom: 8,
          }}>
            Favori Söz
          </label>
          <textarea
            value={favoriteQuote}
            onChange={(e) => setFavoriteQuote(e.target.value.slice(0, 300))}
            placeholder="İlham veren bir söz..."
            maxLength={300}
            rows={4}
            style={{
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              backgroundColor: "#1a1a1a",
              border: "2px solid #114232",
              borderRadius: 12,
              padding: 16,
              color: "#fff",
              fontSize: 16,
              resize: "vertical",
              fontFamily: "inherit",
            }}
          />
          <div style={{
            textAlign: "right",
            color: "#666",
            fontSize: 12,
            marginTop: 4,
          }}>
            {favoriteQuote.length}/300
          </div>
        </div>

        {/* Website */}
        <div style={{ marginBottom: 24 }}>
          <label style={{
            display: "block",
            color: "#114232",
            fontSize: 16,
            fontWeight: 700,
            marginBottom: 8,
          }}>
            Website
          </label>
          <input
            type="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value.slice(0, 200))}
            placeholder="https://example.com"
            maxLength={200}
            style={{
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              backgroundColor: "#1a1a1a",
              border: "2px solid #114232",
              borderRadius: 12,
              padding: 16,
              color: "#fff",
              fontSize: 16,
            }}
          />
        </div>

        {/* Info Box */}
        <div style={{
          backgroundColor: "rgba(17, 66, 50, 0.2)",
          borderRadius: 12,
          padding: 16,
          marginTop: 16,
        }}>
          <p style={{
            color: "#999",
            fontSize: 14,
            lineHeight: 1.5,
            margin: 0,
          }}>
            💡 Bu bilgiler profilinde görünecek. İstersen boş bırakabilirsin.
          </p>
          <p style={{
            color: "#888",
            fontSize: 13,
            lineHeight: 1.5,
            margin: "8px 0 0 0",
          }}>
            ⚙️ Kişisel bilgileri (Ad, Soyad, Telefon, Doğum Tarihi) düzenlemek için <strong style={{ color: "#114232" }}>Ayarlar → Hesap Ayarları</strong> bölümünü kullanabilirsin.
          </p>
        </div>
      </div>
    </div>
  );
}